import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || '';
const token = () => localStorage.getItem('token');

const authFetch = (path, options = {}) =>
  fetch(`${API}/api${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}`, ...(options.headers || {}) }
  }).then(r => r.json());

const EMPTY_EDIT = { name: '', email: '', phone: '', role: 'customer' };

const WA_TEMPLATES = [
  { id: 'greeting',     label: '👋 Greeting',          text: (n) => `Hi ${n}! 😊 Hope you're enjoying your daily health shots from OGH. Stay healthy!` },
  { id: 'delivery',     label: '🚚 Delivery Reminder',  text: (n) => `Hi ${n}! 🥤 Your OGH health shot is ready for pickup today. Don't miss it!` },
  { id: 'reschedule',   label: '📅 Reschedule Deferred', text: (n) => `Hi ${n}! 📅 Your deferred delivery has been rescheduled to [DATE]. Your health shot will be ready for pickup on that day. Thank you for your understanding! 🥤` },
  { id: 'renewal',      label: '🎯 Plan Renewal',        text: (n) => `Hi ${n}! ⏳ Your OGH subscription is ending soon. Renew now to continue your health journey without a break!` },
  { id: 'video',        label: '🥤 Shot Video',          text: (n) => `Hi ${n}! 🎥 Check out this video about your health shot and its amazing benefits: [paste link here]` },
  { id: 'review',       label: '⭐ Review Request',      text: (n) => `Hi ${n}! 🙏 We'd love to hear your experience with OGH. Could you please share a quick review? Your feedback means a lot to us!` },
  { id: 'newproduct',   label: '🆕 New Product Launch',  text: (n) => `Hi ${n}! 🎉 Exciting news! We've just launched a brand new product at OGH. Be the first to try it! Contact us to know more.` },
  { id: 'offer',        label: '🎁 Special Offer',       text: (n) => `Hi ${n}! 🎁 We have an exclusive offer just for you! Get in touch with us to grab this limited-time deal.` },
  { id: 'healthtip',    label: '💊 Health Tip',          text: (n) => `Hi ${n}! 💡 Health Tip of the day: Consistency is key! Taking your daily OGH shots regularly gives the best results. Keep it up! 💪` },
  { id: 'thankyou',     label: '🙏 Thank You',           text: (n) => `Hi ${n}! 🌿 Thank you for being a valued OGH customer. We truly appreciate your trust and support. Have a healthy day!` },
  { id: 'custom',       label: '✍️ Custom Message',      text: () => `` },
];

export default function AdminUserDetails() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(EMPTY_EDIT);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [subscriptions, setSubscriptions] = useState({});
  const [waUser, setWaUser] = useState(null);
  const [waMessage, setWaMessage] = useState('');
  const [waTemplate, setWaTemplate] = useState('');
  const [scheduleModal, setScheduleModal] = useState(null); // holds subscription doc
  const [scheduleProducts, setScheduleProducts] = useState({});
  const [bulkWaModal, setBulkWaModal] = useState(false);
  const [bulkWaMessage, setBulkWaMessage] = useState('');
  const [bulkWaTemplate, setBulkWaTemplate] = useState('');
  const [useDesktopApp, setUseDesktopApp] = useState(localStorage.getItem('useWhatsAppDesktop') === 'true');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filterType, setFilterType] = useState('all'); // 'all', 'active', 'inactive'
  const whatsappTabRef = React.useRef(null);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await authFetch('/_find', {
        method: 'POST',
        body: JSON.stringify({ selector: { type: 'customer' }, limit: 100 })
      });
      const sorted = (data.docs || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setUsers(sorted);

      // Load subscriptions count per user
      const subData = await authFetch('/_find', {
        method: 'POST',
        body: JSON.stringify({ selector: { type: 'user_subscription' }, limit: 200 })
      });
      const subMap = {};
      (subData.docs || []).forEach(s => {
        if (!subMap[s.customerId]) subMap[s.customerId] = [];
        subMap[s.customerId].push(s);
      });
      setSubscriptions(subMap);
    } catch (e) {
      console.error('Error loading users:', e);
    }
    setLoading(false);
  };

  const openWhatsApp = (user) => {
    setWaUser(user);
    setWaTemplate('');
    setWaMessage('');
  };

  const selectTemplate = (tpl, userName) => {
    setWaTemplate(tpl.id);
    setWaMessage(tpl.text(userName || 'there'));
  };

  const sendWhatsApp = () => {
    if (!waUser?.phone) return alert('No phone number for this user.');
    const phone = '91' + waUser.phone.replace(/\D/g, '');
    const baseUrl = useDesktopApp ? 'whatsapp://send' : 'https://web.whatsapp.com/send';
    const url = `${baseUrl}?phone=${phone}&text=${encodeURIComponent(waMessage)}`;
    
    if (useDesktopApp) {
      // For desktop/mobile app, just open the URL (it will launch the app)
      window.location.href = url;
    } else {
      // For web version, check if the tab is still open and valid
      if (whatsappTabRef.current && !whatsappTabRef.current.closed) {
        whatsappTabRef.current.location.href = url;
        whatsappTabRef.current.focus();
      } else {
        whatsappTabRef.current = window.open(url, '_blank');
      }
    }
  };

  const openBulkWhatsApp = () => {
    setBulkWaModal(true);
    setBulkWaTemplate('');
    setBulkWaMessage('');
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filtered.filter(u => u.phone).length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filtered.filter(u => u.phone).map(u => u._id));
    }
  };

  const openSelectedWhatsApp = () => {
    if (selectedUsers.length === 0) return alert('Please select at least one user.');
    setBulkWaModal(true);
    setBulkWaTemplate('');
    setBulkWaMessage('');
  };

  const selectBulkTemplate = (tpl) => {
    setBulkWaTemplate(tpl.id);
    setBulkWaMessage(tpl.text('Customer'));
  };

  const sendBulkWhatsApp = async () => {
    const targetUsers = selectedUsers.length > 0 
      ? filtered.filter(u => selectedUsers.includes(u._id) && u.phone)
      : filtered.filter(u => u.phone);
    
    if (targetUsers.length === 0) return alert('No users with phone numbers found.');
    if (!bulkWaMessage.trim()) return alert('Please enter a message.');
    
    const appType = useDesktopApp ? 'WhatsApp App' : 'WhatsApp Web';
    const confirmed = window.confirm(`Send WhatsApp message to ${targetUsers.length} ${selectedUsers.length > 0 ? 'selected' : ''} users?\n\nThis will open ${appType} for each user with a 3-second delay between each.`);
    if (!confirmed) return;

    setBulkWaModal(false);
    setSelectedUsers([]); // Clear selection after sending

    for (let i = 0; i < targetUsers.length; i++) {
      const user = targetUsers[i];
      const phone = '91' + user.phone.replace(/\D/g, '');
      const personalizedMsg = bulkWaMessage.replace(/Customer|there/gi, user.name || 'Customer');
      const baseUrl = useDesktopApp ? 'whatsapp://send' : 'https://web.whatsapp.com/send';
      const url = `${baseUrl}?phone=${phone}&text=${encodeURIComponent(personalizedMsg)}`;
      
      if (useDesktopApp) {
        // For desktop/mobile app, just open the URL (it will launch the app)
        window.location.href = url;
      } else {
        // For web version, check if the tab is still open and valid
        if (whatsappTabRef.current && !whatsappTabRef.current.closed) {
          whatsappTabRef.current.location.href = url;
          whatsappTabRef.current.focus();
        } else {
          whatsappTabRef.current = window.open(url, '_blank');
        }
      }

      // Wait 3 seconds before next user (except for the last one)
      if (i < targetUsers.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
    }
  };

  const openSchedule = async (sub) => {
    setScheduleModal(sub);
    if (Object.keys(scheduleProducts).length === 0) {
      try {
        const productService = await import('../../db/product-service');
        const allProducts = await productService.default.getAllProducts();
        const map = {};
        allProducts.forEach(p => { map[p._id] = p.name; });
        setScheduleProducts(map);
      } catch (e) { console.error(e); }
    }
  };

  const openEdit = (user) => {
    setEditUser(user);
    setForm({ name: user.name || '', email: user.email || '', phone: user.phone || '', role: user.role || 'customer' });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const updated = { ...editUser, ...form, updatedAt: new Date().toISOString() };
      await authFetch(`/${editUser._id}`, {
        method: 'PUT',
        body: JSON.stringify(updated)
      });
      setUsers(prev => prev.map(u => u._id === editUser._id ? updated : u));
      setEditUser(null);
    } catch (e) {
      alert('Failed to save: ' + e.message);
    }
    setSaving(false);
  };

  const filtered = users.filter(u => {
    // Search filter
    const matchesSearch = u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.phone?.includes(search) ||
      u.email?.toLowerCase().includes(search.toLowerCase());
    
    if (!matchesSearch) return false;

    // Subscription filter
    if (filterType === 'all') return true;
    
    const userSubs = subscriptions[u._id] || [];
    const hasActiveSub = userSubs.some(s => s.status === 'active');
    
    if (filterType === 'active') return hasActiveSub;
    if (filterType === 'inactive') return !hasActiveSub; // includes completed, expired, cancelled, or no subscriptions
    
    return true;
  });

  // Calculate counts for filter buttons
  const activeCount = users.filter(u => {
    const userSubs = subscriptions[u._id] || [];
    return userSubs.some(s => s.status === 'active');
  }).length;
  const inactiveCount = users.length - activeCount;

  const labelStyle = { fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' };
  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <h2 style={{ margin: 0, color: '#2c3e50' }}>👥 User Details</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {selectedUsers.length > 0 && (
            <>
              <button onClick={openSelectedWhatsApp} style={{ padding: '8px 16px', backgroundColor: '#128C7E', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                💬 Send to Selected ({selectedUsers.length})
              </button>
              <button onClick={() => setSelectedUsers([])} style={{ padding: '8px 16px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                ✕ Clear Selection
              </button>
            </>
          )}
          <button onClick={openBulkWhatsApp} style={{ padding: '8px 16px', backgroundColor: '#25D366', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>💬 Send to All</button>
          <button onClick={loadUsers} style={{ padding: '8px 16px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>🔄 Refresh</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Total Users', value: users.length, color: '#2196F3' },
          { label: 'Active Subscriptions', value: Object.values(subscriptions).flat().filter(s => s.status === 'active').length, color: '#4CAF50' },
          { label: 'Completed Subscriptions', value: Object.values(subscriptions).flat().filter(s => s.status === 'completed').length, color: '#9C27B0' },
          { label: 'Admins', value: users.filter(u => u.role === 'admin').length, color: '#FF9800' },
        ].map(s => (
          <div key={s.label} style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 6px rgba(0,0,0,0.08)', borderLeft: `4px solid ${s.color}` }}>
            <div style={{ fontSize: '26px', fontWeight: 'bold', color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Search and Filter */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="🔍 Search by name, phone or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, flex: 1, minWidth: '200px', fontSize: '14px' }}
        />
        <div style={{ display: 'flex', gap: '8px', backgroundColor: '#f8f9fa', padding: '4px', borderRadius: '8px' }}>
          <button
            onClick={() => setFilterType('all')}
            style={{ padding: '8px 16px', backgroundColor: filterType === 'all' ? '#2196F3' : 'transparent', color: filterType === 'all' ? 'white' : '#666', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', transition: 'all 0.2s' }}>
            All ({users.length})
          </button>
          <button
            onClick={() => setFilterType('active')}
            style={{ padding: '8px 16px', backgroundColor: filterType === 'active' ? '#4CAF50' : 'transparent', color: filterType === 'active' ? 'white' : '#666', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', transition: 'all 0.2s' }}>
            Active ({activeCount})
          </button>
          <button
            onClick={() => setFilterType('inactive')}
            style={{ padding: '8px 16px', backgroundColor: filterType === 'inactive' ? '#FF9800' : 'transparent', color: filterType === 'inactive' ? 'white' : '#666', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', transition: 'all 0.2s' }}>
            No Active ({inactiveCount})
          </button>
        </div>
        {filtered.filter(u => u.phone).length > 0 && (
          <button 
            onClick={toggleSelectAll}
            style={{ padding: '8px 16px', backgroundColor: selectedUsers.length === filtered.filter(u => u.phone).length ? '#f44336' : '#2196F3', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap' }}>
            {selectedUsers.length === filtered.filter(u => u.phone).length ? '☐ Deselect All' : '☑ Select All'}
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading users...</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '8px', overflow: 'hidden' }}>
            <thead>
              <tr style={{ backgroundColor: '#4CAF50', color: 'white' }}>
                {['', '#', 'Name', 'Phone', 'Email', 'Role', 'Joined', 'Subscriptions', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((user, idx) => {
                const userSubs = subscriptions[user._id] || [];
                const activeSubs = userSubs.filter(s => s.status === 'active');
                return (
                  <tr key={user._id} style={{ borderBottom: '1px solid #e0e0e0', backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white' }}>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {user.phone && (
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user._id)}
                          onChange={() => toggleUserSelection(user._id)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#25D366' }}
                        />
                      )}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#999' }}>{idx + 1}</td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>{user.name || '—'}</div>
                      <div style={{ fontSize: '11px', color: '#999' }}>{user._id}</div>
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px' }}>{user.phone || '—'}</td>
                    <td style={{ padding: '12px', fontSize: '13px' }}>{user.email || '—'}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', backgroundColor: user.role === 'admin' ? '#FF9800' : '#e8f5e9', color: user.role === 'admin' ? 'white' : '#2e7d32' }}>
                        {user.role || 'customer'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px', color: '#666' }}>{user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-IN') : '—'}</td>
                    <td style={{ padding: '12px' }}>
                      {userSubs.length === 0 ? <span style={{ color: '#999', fontSize: '12px' }}>None</span> : (
                        <div>
                          {activeSubs.length > 0 && <div style={{ fontSize: '12px', color: '#4CAF50', fontWeight: '600' }}>● {activeSubs.length} Active</div>}
                          {userSubs.filter(s => s.status !== 'active').length > 0 && (
                            <div style={{ fontSize: '11px', color: '#999' }}>
                              {userSubs.filter(s => s.status === 'completed').length > 0 && `${userSubs.filter(s => s.status === 'completed').length} Completed`}
                              {userSubs.filter(s => s.status !== 'active' && s.status !== 'completed').length > 0 && ` ${userSubs.filter(s => s.status !== 'active' && s.status !== 'completed').length} Other`}
                            </div>
                          )}
                            {userSubs.map((s, si) => {
                              const statusColors = { active: '#2e7d32', completed: '#FF9800', cancelled: '#f44336', expired: '#999' };
                              const statusBgColors = { active: '#e8f5e9', completed: '#fff3e0', cancelled: '#ffebee', expired: '#f5f5f5' };
                              return (
                                <div key={si} style={{ fontSize: '11px', color: '#555', marginTop: '3px', padding: '4px 6px', backgroundColor: statusBgColors[s.status] || '#f5f5f5', borderRadius: '4px' }}>
                                  <div style={{ fontWeight: '600', color: statusColors[s.status] || '#999' }}>
                                    {s.planType === 'free_trial' ? '🎁 Welcome Gift' : s.planName || s.planType?.replace(/_/g, ' ')}
                                    <span style={{ marginLeft: '4px', fontSize: '10px', padding: '1px 4px', backgroundColor: statusColors[s.status] || '#999', color: 'white', borderRadius: '3px' }}>{s.status?.toUpperCase()}</span>
                                  </div>
                                  <div>📅 Start: {s.startDate ? new Date(s.startDate).toLocaleDateString('en-IN') : '—'}</div>
                                  <div>🔚 End: {s.expiryDate ? new Date(s.expiryDate).toLocaleDateString('en-IN') : s.nextBillingDate ? new Date(s.nextBillingDate).toLocaleDateString('en-IN') : '—'}</div>
                                  <button onClick={() => openSchedule(s)} style={{ marginTop: '4px', padding: '3px 8px', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>📅 View Schedule</button>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <button onClick={() => openEdit(user)} style={{ padding: '6px 12px', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                          ✏️ Edit
                        </button>
                        <button onClick={() => openWhatsApp(user)} style={{ padding: '6px 12px', backgroundColor: '#25D366', color: 'white', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                          💬 WhatsApp
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* WhatsApp Modal */}
      {waUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setWaUser(null)}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '520px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <div style={{ width: '40px', height: '40px', backgroundColor: '#25D366', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>💬</div>
              <div>
                <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '16px' }}>WhatsApp Message</h3>
                <div style={{ fontSize: '12px', color: '#666' }}>{waUser.name} • +91 {waUser.phone}</div>
              </div>
            </div>

            {/* Templates */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Select a Template</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {WA_TEMPLATES.map(tpl => (
                  <button key={tpl.id} onClick={() => selectTemplate(tpl, waUser.name)}
                    style={{ padding: '8px 10px', border: `2px solid ${waTemplate === tpl.id ? '#25D366' : '#e0e0e0'}`, borderRadius: '8px', backgroundColor: waTemplate === tpl.id ? '#e8fdf0' : 'white', cursor: 'pointer', fontSize: '12px', fontWeight: waTemplate === tpl.id ? '700' : '400', textAlign: 'left', color: '#2c3e50', transition: 'all 0.15s' }}>
                    {tpl.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Message Box */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Message</label>
              <textarea
                rows={5}
                value={waMessage}
                onChange={e => setWaMessage(e.target.value)}
                placeholder="Select a template above or type a custom message..."
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.5' }}
              />
              <div style={{ fontSize: '11px', color: '#999', marginTop: '4px', textAlign: 'right' }}>{waMessage.length} characters</div>
              {waTemplate === 'reschedule' && (
                <div style={{ marginTop: '8px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', fontSize: '12px', color: '#856404' }}>
                  💡 <strong>Tip:</strong> Replace [DATE] with the actual rescheduled date (e.g., "15th January 2024" or "Tomorrow")
                </div>
              )}
            </div>

            {/* App/Web Toggle */}
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                <input
                  type="checkbox"
                  checked={useDesktopApp}
                  onChange={(e) => {
                    setUseDesktopApp(e.target.checked);
                    localStorage.setItem('useWhatsAppDesktop', e.target.checked);
                  }}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span style={{ fontWeight: '600', color: '#2c3e50' }}>📱 Use WhatsApp Desktop/Mobile App</span>
              </label>
              <div style={{ fontSize: '11px', color: '#666', marginTop: '4px', marginLeft: '24px' }}>
                {useDesktopApp ? '✓ Will open in installed WhatsApp app' : 'Will open in web browser'}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setWaUser(null)} style={{ flex: 1, padding: '10px', border: '1px solid #bbb', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#f5f5f5', color: '#333', fontSize: '14px', fontWeight: '600' }}>Cancel</button>
              <button onClick={sendWhatsApp} disabled={!waMessage.trim()} style={{ flex: 2, padding: '10px', backgroundColor: waMessage.trim() ? '#25D366' : '#ccc', color: 'white', border: 'none', borderRadius: '6px', cursor: waMessage.trim() ? 'pointer' : 'not-allowed', fontWeight: '600', fontSize: '14px' }}>
                💬 Open in {useDesktopApp ? 'WhatsApp App' : 'WhatsApp Web'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {scheduleModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setScheduleModal(null)}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '680px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#2c3e50' }}>📅 Delivery Schedule</h3>
                <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                  {scheduleModal.planType === 'free_trial' ? '🎁 Welcome Gift' : scheduleModal.planName} &bull; <span style={{ color: scheduleModal.status === 'active' ? '#4CAF50' : '#999', fontWeight: '600' }}>{scheduleModal.status?.toUpperCase()}</span>
                </div>
              </div>
              <button onClick={() => setScheduleModal(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}>×</button>
            </div>

            {/* Stats */}
            {(() => {
              const entries = Object.entries(scheduleModal.deliverySchedule || {}).sort(([a], [b]) => new Date(a) - new Date(b));
              const delivered = entries.filter(([, d]) => d.status === 'delivered').length;
              const pending = entries.filter(([, d]) => d.status === 'pending').length;
              const deferred = entries.filter(([, d]) => d.status === 'deferred' || d.status === 'rescheduled').length;
              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '20px' }}>
                  {[{ label: 'Total', value: entries.length, color: '#2196F3' }, { label: 'Delivered', value: delivered, color: '#4CAF50' }, { label: 'Pending', value: pending, color: '#FF9800' }, { label: 'Deferred', value: deferred, color: '#f44336' }].map(s => (
                    <div key={s.label} style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                      <div style={{ fontSize: '22px', fontWeight: 'bold', color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Schedule Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#4CAF50', color: 'white' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>#</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Date</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Product</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(scheduleModal.deliverySchedule || {}).sort(([a], [b]) => new Date(a) - new Date(b)).map(([dateKey, day], idx) => {
                  const statusColors = { delivered: '#4CAF50', pending: '#FF9800', deferred: '#f44336', rescheduled: '#9C27B0', dispatched: '#2196F3' };
                  const statusEmojis = { delivered: '✅', pending: '⏳', deferred: '⚠️', rescheduled: '📅', dispatched: '🚚' };
                  const productName = scheduleProducts[day.shot] || scheduleProducts[day.smoothie] || 'Product';
                  const isToday = dateKey === new Date().toISOString().split('T')[0];
                  return (
                    <tr key={dateKey} style={{ borderBottom: '1px solid #e0e0e0', backgroundColor: isToday ? '#fff9c4' : idx % 2 === 0 ? '#fafafa' : 'white' }}>
                      <td style={{ padding: '10px', color: '#999' }}>{idx + 1}</td>
                      <td style={{ padding: '10px', fontWeight: isToday ? '700' : '400' }}>
                        {new Date(dateKey).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                        {isToday && <span style={{ marginLeft: '6px', fontSize: '11px', backgroundColor: '#FF9800', color: 'white', padding: '2px 6px', borderRadius: '4px' }}>TODAY</span>}
                      </td>
                      <td style={{ padding: '10px' }}>{productName}</td>
                      <td style={{ padding: '10px', textAlign: 'center' }}>
                        <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: `${statusColors[day.status] || '#999'}20`, color: statusColors[day.status] || '#999' }}>
                          {statusEmojis[day.status] || '•'} {day.status || 'pending'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bulk WhatsApp Modal */}
      {bulkWaModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => { setBulkWaModal(false); setSelectedUsers([]); }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '520px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <div style={{ width: '40px', height: '40px', backgroundColor: '#25D366', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>💬</div>
              <div>
                <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '16px' }}>
                  {selectedUsers.length > 0 ? 'Send to Selected Users' : 'Send to All Users'}
                </h3>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {selectedUsers.length > 0 
                    ? `${filtered.filter(u => selectedUsers.includes(u._id) && u.phone).length} selected users with phone numbers`
                    : `${filtered.filter(u => u.phone).length} users with phone numbers`
                  }
                </div>
              </div>
            </div>

            {/* Warning */}
            <div style={{ padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#856404' }}>
              ⚠️ This will open {useDesktopApp ? 'WhatsApp App' : 'WhatsApp Web'} for each user with a 3-second delay between messages.
            </div>

            {/* App/Web Toggle */}
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                <input
                  type="checkbox"
                  checked={useDesktopApp}
                  onChange={(e) => {
                    setUseDesktopApp(e.target.checked);
                    localStorage.setItem('useWhatsAppDesktop', e.target.checked);
                  }}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span style={{ fontWeight: '600', color: '#2c3e50' }}>📱 Use WhatsApp Desktop/Mobile App</span>
              </label>
              <div style={{ fontSize: '11px', color: '#666', marginTop: '4px', marginLeft: '24px' }}>
                {useDesktopApp ? '✓ Will open in installed WhatsApp app' : 'Will open in web browser'}
              </div>
            </div>

            {/* Templates */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Select a Template</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {WA_TEMPLATES.map(tpl => (
                  <button key={tpl.id} onClick={() => selectBulkTemplate(tpl)}
                    style={{ padding: '8px 10px', border: `2px solid ${bulkWaTemplate === tpl.id ? '#25D366' : '#e0e0e0'}`, borderRadius: '8px', backgroundColor: bulkWaTemplate === tpl.id ? '#e8fdf0' : 'white', cursor: 'pointer', fontSize: '12px', fontWeight: bulkWaTemplate === tpl.id ? '700' : '400', textAlign: 'left', color: '#2c3e50', transition: 'all 0.15s' }}>
                    {tpl.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Message Box */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Message (use "Customer" - it will be replaced with each user's name)</label>
              <textarea
                rows={5}
                value={bulkWaMessage}
                onChange={e => setBulkWaMessage(e.target.value)}
                placeholder="Select a template above or type a custom message..."
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.5' }}
              />
              <div style={{ fontSize: '11px', color: '#999', marginTop: '4px', textAlign: 'right' }}>{bulkWaMessage.length} characters</div>
              {bulkWaTemplate === 'reschedule' && (
                <div style={{ marginTop: '8px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', fontSize: '12px', color: '#856404' }}>
                  💡 <strong>Tip:</strong> Replace [DATE] with the actual rescheduled date (e.g., "15th January 2024" or "Tomorrow")
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setBulkWaModal(false); setSelectedUsers([]); }} style={{ flex: 1, padding: '10px', border: '1px solid #bbb', borderRadius: '6px', cursor: 'pointer', backgroundColor: '#f5f5f5', color: '#333', fontSize: '14px', fontWeight: '600' }}>Cancel</button>
              <button onClick={sendBulkWhatsApp} disabled={!bulkWaMessage.trim()} style={{ flex: 2, padding: '10px', backgroundColor: bulkWaMessage.trim() ? '#25D366' : '#ccc', color: 'white', border: 'none', borderRadius: '6px', cursor: bulkWaMessage.trim() ? 'pointer' : 'not-allowed', fontWeight: '600', fontSize: '14px' }}>
                💬 Send to {selectedUsers.length > 0 ? `${filtered.filter(u => selectedUsers.includes(u._id) && u.phone).length} Selected` : `${filtered.filter(u => u.phone).length}`} Users
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setEditUser(null)}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '480px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px', color: '#2c3e50' }}>✏️ Edit User</h3>
            <div style={{ display: 'grid', gap: '16px' }}>
              {[
                { label: 'Full Name', key: 'name', type: 'text' },
                { label: 'Email', key: 'email', type: 'email' },
                { label: 'Phone', key: 'phone', type: 'tel' },
              ].map(f => (
                <div key={f.key}>
                  <label style={labelStyle}>{f.label}</label>
                  <input type={f.type} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={inputStyle} />
                </div>
              ))}
              <div>
                <label style={labelStyle}>Role</label>
                <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} style={inputStyle}>
                  <option value="customer">Customer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {/* Read-only info */}
              <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', fontSize: '12px', color: '#666' }}>
                <div><strong>ID:</strong> {editUser._id}</div>
                <div><strong>Joined:</strong> {editUser.createdAt ? new Date(editUser.createdAt).toLocaleDateString('en-IN') : '—'}</div>
                <div><strong>Subscriptions:</strong> {(subscriptions[editUser._id] || []).length}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
              <button onClick={() => setEditUser(null)} style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', backgroundColor: 'white', fontSize: '14px' }}>Cancel</button>
              <button onClick={saveEdit} disabled={saving} style={{ flex: 2, padding: '10px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>
                {saving ? 'Saving...' : '✅ Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
